import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import { TooltipPositionEnum } from '@atoms/Tooltip/Tooltip';
import Video from '@atoms/Video/Video';
import { IHotspotItem } from '@models/IHotspotButton';
import { IHotspotCard } from '@models/IHotspotCard';
import { IHotspots, IHotspotsData } from '@models/IHotspots';
import { IWishlistDrawerLabels } from '@models/IWishlistDrawer';
import CardTypeEnum from '@models/enums/CardTypeEnum';
import NewHotspotButton from '@molecules/HotspotButton/HotspotButton';
import InspirationOverlay, {
  IInspirationOverlayLabels,
} from '@organisms/InspirationOverlay/InspirationOverlay';
import { WishlistButtonContext } from '@utility/context/WishlistButtonContext';
import { handleHexCode } from '@utility/helpers/handleHexCode';
import useMediaQuery from '@utility/hooks/useMediaQuery';
import classNames from 'classnames';
import { useContext, useMemo, useState } from 'react';

export const enum HotspotCardVariant {
  shortNoTitle = 'short-no-title',
  tallWithTab = 'tall-with-title',
}

export interface IHotspotCardExtended extends IHotspotCard {
  readonly cardIndex?: number;
  readonly displayInOverlay?: boolean;
  readonly errors?: any;
  readonly hasMoreItemsToShow?: boolean;
  readonly hideMobileHotspots?: boolean;
  readonly initialTooltipIndex?: number;
  readonly inspirationOverlayItems?: IHotspotCard[];
  readonly inspirationOverlayLabels?: IInspirationOverlayLabels;
  readonly link?: string;
  readonly linkText?: string;
  readonly loading?: boolean;
  readonly onRequestMore?: () => void;
  readonly showLikeButton?: boolean;
  readonly title?: string;
  readonly useNewHotspotButton?: boolean;
  readonly variant?: HotspotCardVariant;
  readonly wishlistDrawerLabels?: IWishlistDrawerLabels;
  readonly wishlistId?: number;
}

const HotspotCard = (props: IHotspotCardExtended) => {
  const {
    alternativeText,
    aspectRatio,
    cardIndex,
    desktopImage,
    displayInOverlay,
    errors,
    hasMoreItemsToShow,
    hideMobileHotspots,
    hotspots,
    initialTooltipIndex,
    inspirationOverlayItems,
    inspirationOverlayLabels,
    link,
    linkText,
    loading,
    mobileImage,
    onRequestMore,
    showLikeButton = true,
    useNewHotspotButton = true,
    variant = HotspotCardVariant.shortNoTitle,
    video,
    wishlistDrawerLabels,
    wishlistId,
  } = props;

  const [activeTooltipIndex, setActiveTooltipIndex] = useState(
    initialTooltipIndex ?? -1
  );

  const [videoPlaying, setVideoPlaying] = useState(true);
  const [openOverlayCardIndex, setOpenOverlayCardIndex] = useState<
    number | null
  >(null);

  const hasVideo = !!video?.videoSource;
  const isMobile = useMediaQuery(752);
  const WishlistButton = useContext(WishlistButtonContext);
  const isPortrait = aspectRatio ? aspectRatio <= 1 : true;
  const calculatedAspectRatio =
    variant === HotspotCardVariant.shortNoTitle ? 4 / 5 : 2 / 3;

  const image = isPortrait ? mobileImage : desktopImage;

  const handleHotspotItemsOnNewButton = (current: IHotspots) => {
    const { hotspotItems, colour, product } = current ?? {};
    if (hotspotItems?.length) return hotspotItems;

    if (hasVideo && hotspots?.length) {
      return hotspots.map(({ colour, product }) =>
        mapColourAndProductToNewHotspot(
          colour as IHotspotsData,
          product as IHotspotsData
        )
      );
    }
    return mapColourAndProductToNewHotspot(
      colour as IHotspotsData,
      product as IHotspotsData
    );
  };

  const renderHotspotButton = (hotspot: IHotspots, index: number) => {
    const {
      colour,
      horizontalPercentMobile,
      product,
      verticalPercentMobile,
      horizontalPercentDesktop,
      verticalPercentDesktop,
    } = hotspot ?? {};

    const horizontalPercentage = isPortrait
      ? horizontalPercentMobile
      : horizontalPercentDesktop;

    const verticalPercentage = isPortrait
      ? verticalPercentMobile
      : verticalPercentDesktop;

    const positionStyle = hasVideo
      ? { bottom: displayInOverlay && !isMobile ? '52px' : 0, right: 0 }
      : {
          left: `${horizontalPercentage}%`,
          top: `${verticalPercentage}%`,
        };

    const id = product?.name || colour?.name || 'hotspot';

    return (
      <div
        key={`${id}-${horizontalPercentage}-${verticalPercentage}`}
        className={classNames(
          'tw-absolute -tw-translate-x-1/2 -tw-translate-y-1/2',
          index === activeTooltipIndex ? 'tw-z-20' : 'tw-z-10'
        )}
        style={positionStyle}
      >
        {useNewHotspotButton ? (
          <NewHotspotButton
            id={id}
            isActive={index === activeTooltipIndex}
            onClick={(val) => {
              if (typeof val !== 'undefined') {
                setActiveTooltipIndex(val ? index : -1);
              } else {
                setActiveTooltipIndex((prevIndex) =>
                  prevIndex === index ? -1 : index
                );
              }
            }}
            onFocus={() => setActiveTooltipIndex(index)}
            tooltipPosition={
              verticalPercentage < 50
                ? TooltipPositionEnum.Bottom
                : TooltipPositionEnum.Top
            }
            tooltipOverflowLeft={horizontalPercentage < 35 && !hasVideo}
            tooltipOverflowRight={horizontalPercentage > 65 || hasVideo}
            tooltipShouldStack={hasVideo}
            hotspotItems={
              handleHotspotItemsOnNewButton(hotspot) as IHotspotItem[]
            }
            disableBlur={displayInOverlay}
          />
        ) : (
          <Button
            buttonClasses="tw-w-md !tw-h-md !tw-p-0 tw-z-10 tw-bg-opacity-50 !tw-ring-0 focus:!tw-ring-offset-0 after:tw-content-none"
            iconClasses="tw-opacity !tw-h-auto !tw-w-2 tw-shrink-0"
            iconName="plus"
            onClick={() => {
              setOpenOverlayCardIndex(cardIndex ?? null);
              setActiveTooltipIndex(index);
            }}
            variant="white"
            aria-label="More information"
          />
        )}
      </div>
    );
  };

  const mapColourAndProductToNewHotspot = (
    colour: IHotspotsData,
    product: IHotspotsData
  ) => {
    const items: IHotspotItem[] = [];
    // TODO: align hotspot data to use product and colour cards

    if (colour) {
      items.push({
        name: colour.colourCard?.colourName ?? colour.name ?? '',
        link: {
          url: colour.colourCard?.link ?? colour.url ?? '',
          text: colour.colourCard?.colourName ?? colour.name ?? '',
        },
        id: colour.colourCard?.id ?? colour.name,
        category: colour.colourCard?.colourCode ?? colour.category ?? '',
        image: colour.colourCard?.mainImage ?? colour.image,
        wishlistDrawerLabels:
          colour.colourCard?.wishlistDrawerLabels ?? undefined,
        colourCode: colour.colourCard?.colourCode ?? colour.colourCode,
        colourHexCode: handleHexCode(
          colour.colourCard?.colourHexCode ?? colour.hexCode
        ),
        colourCard: colour.colourCard ?? undefined,
      });
    }

    if (product) {
      items.push({
        name: product.productCard?.productName ?? product.name ?? '',
        link: product.productCard?.cta ?? {
          url: product.url ?? '',
          text: '',
        },
        id: product.productCard?.productName ?? product.name,
        category: product.productCard?.category ?? product.category ?? '',
        image: product.productCard
          ? {
              ...product.productCard?.productImage.image,
              altText: product.productCard?.productImage.alternativeText,
            }
          : product.image,
        wishlistDrawerLabels:
          product.productCard?.wishlistDrawerLabels ?? undefined,
        productCard: product.productCard ?? undefined,
      });
    }

    return items;
  };

  const getHotspotsToRender = () => {
    if (!hotspots?.length) return [];

    if (hasVideo) {
      // Render only the first hotspot if a video is present
      return [renderHotspotButton(hotspots[0]!, 0)];
    } else {
      // Render all hotspots otherwise
      return hotspots.map(renderHotspotButton);
    }
  };

  const renderHotspots = useMemo(getHotspotsToRender, [
    hotspots,
    hasVideo,
    activeTooltipIndex,
    isPortrait,
  ]);

  const isLink = isMobile && hideMobileHotspots && useNewHotspotButton;
  const Wrapper = isLink ? 'a' : 'div';

  return (
    <>
      <Wrapper
        className={classNames(
          'tw-h-full tw-text-black',
          variant === HotspotCardVariant.tallWithTab &&
            'tw-pb-[60px] md:tw-pb-[76px]',
          !aspectRatio && 'tw-bg-white',
          isLink && 'focus:tw-outline-grey-100'
        )}
        href={isLink ? link : undefined}
        aria-label={isLink ? linkText : undefined}
      >
        <div
          className={classNames('tw-relative tw-h-auto tw-overflow-hidden')}
          style={{ aspectRatio: aspectRatio || calculatedAspectRatio }}
        >
          {showLikeButton && WishlistButton && (
            <WishlistButton
              wishlistId={wishlistId}
              card={{ ...props, aspectRatio: undefined } as IHotspotCard}
              cardId={`${link || hotspots[0]?.product?.url}.html` || ''} // To match existing format
              collection={CardTypeEnum.Inspirations}
              wishlistDrawerLabels={wishlistDrawerLabels}
            />
          )}
          <div className={hideMobileHotspots ? 'max-md:tw-hidden' : undefined}>
            {!!hotspots.length && renderHotspots}
          </div>
          <div
            className={classNames('tw-h-full', {
              'tw-cursor-pointer': !useNewHotspotButton,
            })}
            onClick={() => {
              if (!useNewHotspotButton) {
                setOpenOverlayCardIndex(cardIndex ?? null);
                setActiveTooltipIndex(0);
              }
            }}
          >
            {image && (
              <Image
                imageClasses="tw-w-full"
                image={image}
                sizes="50vw"
                aspectRatio={aspectRatio || calculatedAspectRatio}
                alternativeText={alternativeText || ''}
              />
            )}
            {hasVideo && (
              <div className="tw-relative tw-h-full">
                {displayInOverlay ? (
                  <div className="tw-absolute tw-left-2.5 tw-top-[50px] md:tw-left-4 md:tw-top-14">
                    <Button
                      variant="white"
                      modifier="fill"
                      iconName={videoPlaying ? 'pause' : 'play'}
                      buttonClasses="tw-w-sm !tw-h-sm md:tw-w-md md:!tw-h-md tw-z-20"
                      iconClasses="tw-shrink-0 tw-absolute"
                      onClick={() => setVideoPlaying(!videoPlaying)}
                      aria-label={videoPlaying ? 'Pause video' : 'Play video'}
                    />
                  </div>
                ) : (
                  <button
                    className="tw-absolute tw-right-0 tw-top-0 tw-z-20 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-bg-white/40"
                    onClick={(e) => {
                      e.stopPropagation();
                      setVideoPlaying(!videoPlaying);
                    }}
                    aria-label={videoPlaying ? 'Pause video' : 'Play video'}
                  >
                    <Icon
                      name={videoPlaying ? 'pause' : 'play'}
                      className="!tw-h-auto !tw-w-2"
                    />
                  </button>
                )}

                <Video
                  videoSource={video.videoSource}
                  videoAttributes={{
                    playsinline: true,
                    muted: true,
                    playing: videoPlaying,
                    loop: true,
                  }}
                  controls={false}
                  classes="tw-w-full tw-h-full [&_video]:tw-object-center [&_video]:tw-object-cover tw-inset-0"
                />
              </div>
            )}
          </div>
        </div>
      </Wrapper>
      {!!inspirationOverlayItems && (
        <InspirationOverlay
          activeTooltipIndex={activeTooltipIndex}
          errors={errors}
          hasMoreItemsToShow={hasMoreItemsToShow}
          initialIndex={cardIndex}
          inspirationOverlayLabels={inspirationOverlayLabels}
          isMobile={isMobile}
          isOpen={openOverlayCardIndex === cardIndex}
          setIsOpen={(isOpen) =>
            setOpenOverlayCardIndex(isOpen ? cardIndex ?? null : null)
          }
          items={inspirationOverlayItems}
          loading={loading}
          onRequestMore={onRequestMore}
        />
      )}
    </>
  );
};

export default HotspotCard;
